import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { EnsurePluralization, Capitalize, KebabCase } from '../../util/StringUtil';
import ConstructionCard from '../ConstructionCard';
import Display50Pct50Pct from '../Layout/Display/Display50Pct50Pct';
import { getInitialFilters } from './functions';
import DateFilter from './DateFilter';
import Chip from '../Chip';

import i18n from '../../i18n/translate';

const SelectionCriteriaFilter = props => {
  const {
    dynamicFilter,
    estimateConfig,
    explainerText,
    filterConfig,
    filterOrder,
    formattedFilters,
    onFilterChange,
    title
  } = props;

  const [filters, setFilters] = useState(() => {
    if(filterConfig && dynamicFilter) {
      return getInitialFilters(filterConfig, dynamicFilter)
    }
    return {}
  });
  const [expandedFilters, setExpandedFilters] = useState([]);

  useEffect(() => {
    if(onFilterChange) {
      onFilterChange(filters);
    }
  }, [filters]);

  const onTogglePanel = (expanded, expandedFilter) => {
    if (expanded) {
      setExpandedFilters([...expandedFilters, expandedFilter]);
    } else {
      setExpandedFilters(expandedFilters.filter(filter => filter !== expandedFilter));
    }
  };

  const getFilterList = () => {
    return (
      <>
        {filters.map((filter) => {
          const FilterComponent = filter.type;
          return (
            <ConstructionCard
              title={filter.label}
              onExpandedToggle={expanded => onTogglePanel(expanded, filter.name)}
              data-action="toggle-filter-expand"
              data-object-type={filter.dataAttribute}>
              <FilterComponent
                {...filter}
                key={filter.name}
                name={filter.label}
                onChange={(value, action) => handleFilterChange(filter, value, action)}
                disabled={!!filter.disabled}
                isWrapper={true}
                clearable={false} />
            </ConstructionCard>
          )
        })}
      </>
    )
  }

  const visibleFilters = useMemo(() => filterOrder.map((filterName) => formattedFilters[filterName]).filter(Boolean), [formattedFilters]);

  const renderFilterValue = (value, key) => (
    <div className="selection-criteria-chips" key={key}>
      {Array.isArray(value) ?
        value.map((item, index) => (
          <>
            <Chip value={item} canClose={false} />
            {index < value.length - 1 && <div className="chip-spacer">{i18n.t('or')}</div>}
          </>
        )) :
        <Chip value={value} canClose={false} />}
    </div>
  );

  const renderFilterLabel = (label) => {
    label = label || '';
    label = label.trim();
    if (label.length && label[label.length - 1] === ':') {
      label = label.substring(0, label.length - 1);
    }
    return (
      <div className="selection-criteria-label" data-object-type={`${KebabCase(label)}-selected`}>
        {label}
      </div>
    );
  };

  const formatSelectionCriteria = () => {
    return (
      visibleFilters.map((filter, filtersIndex) => (
        <div className="selection-criteria-display" key={filtersIndex}>
          <>
            {Array.isArray(filter) ?
              filter.map(({ label, value }, filterIndex) => (
                <>
                  {renderFilterLabel(label)}
                  {renderFilterValue(value, filterIndex)}
                  {filterIndex < filter.length - 1 && <div className="criteria-spacer">{i18n.t('and')}</div>}
                </>
              )) :
              <>
                {renderFilterLabel(filter.label)}
                {renderFilterValue(filter.value)}
              </>
            }
            {filtersIndex < visibleFilters.length - 1 && <div className="criteria-spacer">{i18n.t('and')}</div>}
          </>
        </div>
      ))
    );
  }

  const handleFilterChange = (updatedFilter, value) => {
    const newFilters = filters.map(filter => {
      if (filter.name === updatedFilter.name) {
        if(filter.type === DateFilter) {
          filter.value = { ...filter.value, ...value };
        } else {
          filter.value = value;
        }
      }
      return filter;
    });
    setFilters(newFilters);
  }

  const formatEstimateSection = () => {
    const { estimateLabel, estimateCount, estimateEntity, estimateSubtext } = estimateConfig;
    return (
      <>
        {estimateCount >= 0 &&
        <>
          <div className="selection-criteria-estimate-label">{i18n.t(estimateLabel)}</div>
          <div className="selection-criteria-estimate-count">{estimateCount} {Capitalize(EnsurePluralization(estimateEntity, estimateCount))}</div>
          <div className="selection-criteria-subtext">{i18n.t(estimateSubtext)}</div>
        </>}
      </>
    )
  }

  const getFilterSelections = () => {
    return (
      <>
        {explainerText && <div className="selection-criteria-explainer">{i18n.t(explainerText)}</div>}
        {estimateConfig && formatEstimateSection()}
        {formatSelectionCriteria()}
      </>
    )
  }

  return (
    <ConstructionCard 
      title={title}
      className="selection-criteria-card"
      alwaysExpanded={true}>
      <Display50Pct50Pct>
        {getFilterList()}
        {getFilterSelections()}
      </Display50Pct50Pct>
    </ConstructionCard>
  );
}

SelectionCriteriaFilter.propTypes = {
  dynamicFilter: PropTypes.object,
  explainerText: PropTypes.string,
  estimateConfig: PropTypes.shape({
    estimateLabel: PropTypes.string,
    estimateCount: PropTypes.number,
    estimateSubtext: PropTypes.string,
    estimateEntity: PropTypes.string
  }),
  filterConfig: PropTypes.array.isRequired,
  filterOrder: PropTypes.array.isRequired,
  formattedFilters: PropTypes.object.isRequired,
  onFilterChange: PropTypes.func,
  title: PropTypes.string
};

SelectionCriteriaFilter.defaultProps = {
  dynamicFilter: {},
  filterOrder: [],
  filterConfig: [],
  formattedFilters: {},
  title: i18n.t("Selection Criteria")
}

export default SelectionCriteriaFilter;
